import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import html2pdf from 'html2pdf.js';
import { 
  createProposal, 
  generateProposalTemplate,
} from '../../store/slices/proposalsSlice';
import { fetchCategories } from '../../store/slices/leadsSlice';
import { showToast } from '../utils/Toast';
import AddScopeDialog from './AddScopeDialog';
import AddCostCodeDialog from './AddCostCodeDialog';
import ProposalWorksheetTab from './ProposalWorksheetTab';
import ProposalDisplaySettings from './ProposalDisplaySettings';
import { calculateProposalTotals } from './utils/ProposalCalculations';
import { useProposalHandlers } from './utils/ProposalHandlers';
import { config } from '../../config/config';

const CreateProposalDialog = ({ isOpen, onClose, lead, editingProposal = null }) => {
  const dispatch = useDispatch();
  const { categories } = useSelector(state => state.leads);
  const [activeTab, setActiveTab] = useState('worksheet');
  
  // Add utility function to decode HTML entities
  const decodeHtmlEntities = (str) => {
    if (!str) return str;
    
    // Create a temporary DOM element to decode HTML entities
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = str;
    return tempDiv.textContent || tempDiv.innerText || str;
  };
  
  // Helper functions
  const isEditingDisabled = (status) => {
    return ['released', 'approved_proposal_owner', 'rejected_proposal_owner', 'customer_rejected', 'customer_approved'].includes(status);
  };

  const isReleaseDisabled = (status) => {
    return ['customer_rejected', 'customer_approved'].includes(status);
  };

  // Add a variable to check if the proposal is in released state
  const isReleased = editingProposal?.status === 'released';

  // State declarations
  const [formData, setFormData] = useState({
    title: editingProposal?.title ? decodeHtmlEntities(editingProposal.title) : `Proposal for ${lead?.client?.name || lead?.leadContact?.name || 'Client'}`,
    category: editingProposal?.category?._id || '',
    items: editingProposal ? [
        ...(editingProposal.scopeOfWork || []).map(item => ({
          ...item,
          type: 'scope',
          _id: item.costGroup?._id || item._id,
          item: item.costGroup?.name || item.item || '',
          description: item.costGroup?.description || item.description || '',
          photos: item.photos || item.costGroup?.photos || []
        })),
        ...(editingProposal.costCodes || []).map(item => ({
          ...item,
          type: 'cost',
          _id: item.costItem?._id || item._id,
          item: item.costItem?.item || item.item || '',
          description: item.costItem?.description || item.description || ''
        }))
    ] : [],
    totalClientPrice: editingProposal?.clientPrice || 0,
    totalBuilderCost: 0,
    estimatedProfit: 0,
    proposalId: editingProposal?._id || null
  });
  
  const [showAddScope, setShowAddScope] = useState(false);
  const [showAddCostCode, setShowAddCostCode] = useState(false);
  const [currentProposal, setCurrentProposal] = useState(editingProposal);
  const [expandedItems, setExpandedItems] = useState({});

  // Display settings
  const [displaySettings, setDisplaySettings] = useState({
    showItem: true,
    showQuantity: false,
    showUnit: false,
    showPrice: false,
    showCostCodes: false,
    showScopeDescription: true,
    showCostCodeDescription: false,
    expandGroups: false,
    separateGroups: false
  });

  // Function to create initial draft proposal
  const createInitialDraftProposal = async () => {
    if (!currentProposal && !editingProposal) {
      try {
        if (!formData.category) {
          showToast('Please select a category first', 'error');
          return null;
        }

        // Create initial proposal with required fields
        const initialProposal = {
          title: formData.title || `Proposal for ${lead.client?.name || lead.leadContact?.name || 'Client'}`,
          // Use client email from lead data, with fallbacks
          emailAddress: lead.client?.email || lead.clientEmail || lead.leadContact?.email || '',
          status: 'draft',
          category: formData.category,
          lead: lead._id, // Add the lead ID
          scopeOfWork: [],
          costCodes: [],
          costGroups: [],
          // Add any other required fields
          clientPrice: 0,
          builderCost: 0,
          estimatedProfit: 0,
          displaySettings: {
            showItem: true,
            showQuantity: false,
            showUnit: false,
            showPrice: false,
            showCostCodes: false,
            showScopeDescription: true,
            showCostCodeDescription: false,
            expandGroups: false,
            showGroupTotals: true,
            separateGroups: false,
            showSeparateTotals: true
          }
        };

        // Validate required fields
        if (!initialProposal.emailAddress) {
          showToast('Client email address is required', 'error');
          return null;
        }

        if (!initialProposal.lead) {
          showToast('Lead ID is required', 'error');
          return null;
        }

        if (!initialProposal.category) {
          showToast('Category is required', 'error');
          return null;
        }
        
        const result = await dispatch(createProposal({ 
          leadId: lead._id, 
          proposalData: initialProposal 
        })).unwrap();
        
        setCurrentProposal(result.proposal);
        return result.proposal;
      } catch (error) {
        console.error('Failed to create initial draft:', error);
        showToast(error.message || 'Failed to create proposal draft', 'error');
        return null;
      }
    }
    return editingProposal || currentProposal;
  };

  // Fetch categories when dialog opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCategories());
    }
  }, [isOpen, dispatch]);

  // Function to map proposal data to worksheet format
  const mapProposalToWorksheet = (proposal) => {
    if (!proposal) return [];

    const items = [];

    // Map scope of work items with their associated cost codes
    if (proposal.scopeOfWork?.length > 0) {
      proposal.scopeOfWork.forEach(scope => {
        // Create the scope item
        const scopeItem = {
          type: 'scope',
          _id: scope.costGroup?._id,
          costGroup: scope.costGroup,
          item: scope.costGroup?.name || '',
          description: scope.costGroup?.description || '',
          builderCost: scope.costGroup?.builderCost || 0,
          markup: scope.costGroup?.markup || 49.25,
          clientPrice: scope.costGroup?.clientPrice || 0,
          margin: scope.costGroup?.margin || 33,
          photos: scope.photos || scope.costGroup?.photos || [], // Include photos from scope or cost group
          costCodes: []
        };
        
        // Add cost codes associated with this scope item if they exist
        if (scope.costItems && scope.costItems.length > 0) {
          scopeItem.costCodes = scope.costItems.map(costItem => ({
            _id: costItem._id,
            item: costItem.item || '',
            description: costItem.description || '',
            quantity: costItem.quantity || 1,
            unit: costItem.unit !== undefined && costItem.unit !== '' ? costItem.unit : 'EA',
            unitCost: costItem.unitCost || 0,
            builderCost: costItem.builderCost || 0,
            markup: costItem.markup || 49.25,
            clientPrice: costItem.clientPrice || 0,
            margin: costItem.margin || 33
          }));
        }
        
        items.push(scopeItem);
      });
    }

    // Map individual cost code items (not part of any group)
    if (proposal.costCodes?.length > 0) {
      items.push(...proposal.costCodes.map(code => ({
        type: 'cost',
        _id: code.costItem?._id,
        costItem: code.costItem,
        item: code.costItem?.item || '',
        description: code.costItem?.description || '',
        quantity: code.costItem?.quantity || 1,
        unit: code.costItem?.unit !== undefined && code.costItem?.unit !== '' ? code.costItem?.unit : 'EA',
        unitCost: code.costItem?.unitCost || 0,
        builderCost: code.costItem?.builderCost || 0,
        markup: code.costItem?.markup || 49.25,
        clientPrice: code.costItem?.clientPrice || 0,
        margin: code.costItem?.margin || 33
      })));
    }

    return items;
  };

  // Update when editing proposal data
  useEffect(() => {
    if (editingProposal) {
      setCurrentProposal(editingProposal);
      const mappedItems = mapProposalToWorksheet(editingProposal);
      
      // Calculate totals for the mapped items
      const totals = calculateProposalTotals(mappedItems);
      
      setFormData(prev => ({
        ...prev,
        title: editingProposal.title ? decodeHtmlEntities(editingProposal.title) : `Proposal for ${lead?.client?.name || lead?.leadContact?.name || 'Client'}`,
        category: editingProposal.category?._id || '',
        items: mappedItems,
        totalClientPrice: totals.totalClientPrice,
        totalBuilderCost: totals.totalBuilderCost,
        estimatedProfit: totals.estimatedProfit
      }));
      
      // Update display settings separately with proper defaults
      setDisplaySettings(prev => ({
        showItem: true,
        showQuantity: false,
        showUnit: false,
        showPrice: false,
        showCostCodes: false,
        showScopeDescription: true,
        showCostCodeDescription: false,
        expandGroups: false,
        separateGroups: false,
        ...editingProposal.displaySettings
      }));
    }
  }, [editingProposal]);

  // Import the remaining handler functions
  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        type: 'scope', // Default to scope of work
        item: '',
        description: '',
        quantity: 1,
        unit: 'SQ',
        unitCost: 0,
        builderCost: 0,
        markup: 49.25, // Default markup percentage
        clientPrice: 0,
        margin: 33    // Default margin percentage
      }]
    }));
  };

  // Import from ProposalHandlers
  const {
    handleItemChange,
    handleAddScopeOfWork,
    handleAddCostCodes,
    handleSaveCostGroup,
    handleSaveCostItem,
    handleDeleteItem,
    handleSaveAsDraft,
    handleSaveAndRelease,
    handleSaveAndManualApprove,
    handleDisplaySettingChange,
    handleUpdateReleasedProposal
  } = useProposalHandlers({
    lead,
    formData,
    setFormData,
    currentProposal,
    setCurrentProposal,
    displaySettings,
    setDisplaySettings,
    setShowAddScope,
    setShowAddCostCode,
    onClose,
    calculateProposalTotals,
    createInitialDraftProposal
  });

  // Add this to handle toggling item expansion
  const toggleItemExpansion = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Update expandedItems when items change
  useEffect(() => {
    setExpandedItems(prev => {
      const updatedState = {...prev};
      formData.items.forEach((item, index) => {
        // Always set cost items to expanded by default if not already set
        if (item.type === 'cost' && updatedState[index] === undefined) {
          updatedState[index] = true;
        }
      });
      return updatedState;
    });
  }, [formData.items]);

  // Print functionality
  const handlePrint = () => {
    // Create a container for the print content
    const printContainer = document.createElement('div');
    printContainer.className = 'proposal-print-container';

    // Add header information
    const header = document.createElement('div');
    header.className = 'proposal-header';
    header.innerHTML = `
      <h1 class="text-2xl font-bold mb-4">${currentProposal?.title || `Proposal for ${lead.client?.name || lead.leadContact?.name || 'Client'}`}</h1>
      <div class="grid grid-cols-2 gap-4 mb-6">
        <div>
          <p class="font-semibold">Client Information:</p>
          <p>${lead.client?.name || lead.leadContact?.name || ''}</p>
          <p>${lead.client?.email || lead.leadContact?.email || ''}</p>
          <p>${lead.client?.cellPhone || lead.leadContact?.phone || ''}</p>
        </div>
        <div>
          <p class="font-semibold">Project Location:</p>
          <p>${lead.address?.street || ''}</p>
          <p>${lead.address?.city || ''}, ${lead.address?.state || ''} ${lead.address?.zipCode || ''}</p>
        </div>
      </div>
    `;
    printContainer.appendChild(header);

    // Add items table
    const table = document.createElement('table');
    table.className = 'w-full';
    
    // Create table header based on display settings
    let headerRow = '<tr>';
    if (displaySettings.showItem) headerRow += '<th>Item</th>';
    if (displaySettings.showDescription) headerRow += '<th>Description</th>';
    if (displaySettings.showQuantity) headerRow += '<th>Quantity</th>';
    if (displaySettings.showUnit) headerRow += '<th>Unit</th>';
    if (displaySettings.showPrice) headerRow += '<th>Price</th>';
    headerRow += '</tr>';
    
    // Create table body with items
    const tableBody = formData.items.map(item => {
      let row = '<tr>';
      if (displaySettings.showItem) row += `<td>${item.item || ''}</td>`;
      if (displaySettings.showDescription) row += `<td>${item.description || ''}</td>`;
      if (displaySettings.showQuantity) row += `<td>${item.quantity || ''}</td>`;
      if (displaySettings.showUnit) row += `<td>${item.unit || ''}</td>`;
      if (displaySettings.showPrice) row += `<td>$${(item.clientPrice || 0).toFixed(2)}</td>`;
      row += '</tr>';
      return row;
    }).join('');

    // Add total row
    const totalRow = `
      <tr class="font-bold">
        <td colspan="${Object.values(displaySettings).filter(Boolean).length - 1}" class="text-right">Total:</td>
        <td>$${formData.totalClientPrice.toFixed(2)}</td>
      </tr>
    `;

    table.innerHTML = `
      <thead>${headerRow}</thead>
      <tbody>${tableBody}</tbody>
      <tfoot>${totalRow}</tfoot>
    `;
    printContainer.appendChild(table);

    // Add footer with signature space if proposal is approved
    if (currentProposal?.status === 'customer_approved' && currentProposal?.signature) {
      const footer = document.createElement('div');
      footer.className = 'proposal-footer';
      footer.innerHTML = `
        <div class="mt-8">
          <h4 class="font-bold mb-4">Approval Details</h4>
          <p>Approved By: ${currentProposal.signature.name}</p>
          <p>Date: ${new Date(currentProposal.signature.date).toLocaleDateString()}</p>
          ${currentProposal.signature.signatureImage ? `
            <div class="mt-4">
              <p class="mb-2">Signature:</p>
              <img src="${currentProposal.signature.signatureImage}" alt="Signature" style="max-height: 60px;">
            </div>
          ` : ''}
        </div>
      `;
      printContainer.appendChild(footer);
    }

    // Print the container
    import('../../utils/PrintUtils').then(module => {
      module.printElement(printContainer);
    });
  };

  // Helper function to convert image to base64 via proxy
  const convertImageViaProxy = async (imgUrl) => {
    try {
      if (imgUrl.startsWith('data:')) return imgUrl;
      
      const proxyUrl = `${config.apiUrl}/api/proposals/proxy-image?url=${encodeURIComponent(imgUrl)}`;
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        console.warn('Proxy response not ok:', response.status, response.statusText);
        return null;
      }
      
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Failed to convert image via proxy:', imgUrl, error);
      return null;
    }
  };

  // Add handleDownloadProposal function
  const handleDownloadProposal = async () => {
    // Create a temporary loading indicator
    const loadingIndicator = document.createElement('div');
    loadingIndicator.textContent = 'Preparing PDF...';
    loadingIndicator.style.position = 'fixed';
    loadingIndicator.style.top = '50%';
    loadingIndicator.style.left = '50%';
    loadingIndicator.style.transform = 'translate(-50%, -50%)';
    loadingIndicator.style.padding = '20px';
    loadingIndicator.style.background = 'rgba(0,0,0,0.7)';
    loadingIndicator.style.color = 'white';
    loadingIndicator.style.borderRadius = '5px';
    loadingIndicator.style.zIndex = '9999';
    document.body.appendChild(loadingIndicator);

    try {
      // Get the proposal content
      const proposalElement = document.getElementById('proposal-preview-content');
      
      if (!proposalElement) {
        showToast('Unable to find proposal content', 'error');
        document.body.removeChild(loadingIndicator);
        return;
      }

      // Store original image sources to restore later
      const originalSources = new Map();
      const images = proposalElement.querySelectorAll('img');
      console.log('Found', images.length, 'images to convert');
      
      // Convert all images to base64 IN PLACE (on the original element)
      for (const img of images) {
        const src = img.getAttribute('src') || img.src;
        if (!src || src.startsWith('data:')) continue;
        
        // Store original source
        originalSources.set(img, src);
        
        console.log('Converting image:', src);
        try {
          const base64 = await convertImageViaProxy(src);
          if (base64) {
            img.src = base64;
            console.log('Image converted successfully');
          }
        } catch (err) {
          console.warn('Image conversion failed:', src, err);
        }
      }

      // Wait a bit for images to render
      await new Promise(resolve => setTimeout(resolve, 100));

      // Add temporary CSS for better page breaks
      const styleElement = document.createElement('style');
      styleElement.id = 'pdf-page-break-styles';
      styleElement.textContent = `
        #proposal-preview-content {
          font-size: 12px !important;
        }
        #proposal-preview-content h1, 
        #proposal-preview-content h2, 
        #proposal-preview-content h3,
        #proposal-preview-content h4 {
          page-break-after: avoid !important;
          break-after: avoid !important;
        }
        #proposal-preview-content p,
        #proposal-preview-content li,
        #proposal-preview-content div {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        #proposal-preview-content table {
          page-break-inside: auto !important;
        }
        #proposal-preview-content tr {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        #proposal-preview-content thead {
          display: table-header-group !important;
        }
        #proposal-preview-content img {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          max-height: 250px !important;
        }
        #proposal-preview-content .grid {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        #proposal-preview-content section,
        #proposal-preview-content article {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
      `;
      document.head.appendChild(styleElement);

      // Configure PDF options with page break handling
      const options = {
        margin: [15, 15, 15, 15],
        filename: `proposal-${lead.jobNumber || 'draft'}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          scrollY: 0,
          scrollX: 0
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait'
        },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: ['tr', 'td', 'th', 'img', '.grid', 'li', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'section', 'article', '.rounded-lg']
        }
      };

      // Generate PDF from the ORIGINAL element (with converted images)
      await html2pdf().set(options).from(proposalElement).save();
      
      // Remove temporary styles
      document.head.removeChild(styleElement);
      
      // Restore original image sources
      for (const [img, originalSrc] of originalSources) {
        img.src = originalSrc;
      }
      
      // Clean up
      document.body.removeChild(loadingIndicator);
      showToast('Proposal downloaded successfully', 'success');
      
    } catch (error) {
      console.error('Error in handleDownloadProposal:', error);
      if (document.body.contains(loadingIndicator)) {
        document.body.removeChild(loadingIndicator);
      }
      showToast('Error downloading proposal', 'error');
    }
  };

  // Render the proposal template
  const renderTemplate = () => {
    try {
      // Make sure we have all required data
      if (!lead || !formData) {
        return <div>Loading...</div>;
      }

      // Create a proposal object with current data - ensure title is always taken from formData for real-time updates
      const currentProposalData = {
        ...currentProposal,
        title: formData.title || `Proposal for ${lead.client?.name || lead.leadContact?.name || 'Client'}`,
        emailAddress: lead.client?.email || lead.clientEmail || lead.leadContact?.email || ''
      };

      // Filter items based on display settings and ensure type is set
      const processedItems = formData.items.map(item => {
        const processedItem = {
          ...item,
          type: item.type || (item.costGroup ? 'scope' : 'cost'),
          description: item.description || (item.costGroup?.description || item.costItem?.description || ''),
          photos: item.photos || item.costGroup?.photos || []
        };
        return processedItem;
      });

      // Generate the template with all required parameters
      const htmlContent = generateProposalTemplate(
        lead,
        currentProposalData,
        processedItems,
        displaySettings
      );

      // Return the HTML content using dangerouslySetInnerHTML with an ID for PDF generation
      return <div id="proposal-preview-content" dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    } catch (error) {
      console.error('Error in renderTemplate:', error);
      return <div>Error rendering template</div>;
    }
  };

  const labelClasses = "block text-xs uppercase text-gray-500 mb-1";
  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white
  `;

  return (
    <>
      <div 
        className={`fixed inset-0 z-50 bg-white transform transition-transform duration-300 ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header with Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex justify-between items-center px-6 py-4">
              <h2 className="text-xl font-semibold">
                {editingProposal ? 'Edit Proposal' : 'Create New Proposal'}
              </h2>
              <button 
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Proposal Title */}
            <div className="px-6 py-4 border-b border-gray-100">
              <label className={labelClasses}>Proposal Title</label>
              <input
                type="text"
                className={inputClasses}
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter proposal title"
              />
            </div>

            {/* Category Selection */}
            <div className="px-6 py-4">
              <label className={labelClasses}>Category</label>
              <select
                className={inputClasses}
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="">Select a category</option>
                {categories?.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {!formData.category && (
                <p className="mt-1 text-xs text-red-500">
                  Please select a category for this proposal
                </p>
              )}
            </div>

            {/* Tabs */}
            <div className="px-6">
              <nav className="flex -mb-px">
                {['worksheet', 'proposal preview'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`mr-8 py-4 px-1 border-b-2 font-medium text-sm
                      ${activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'worksheet' ? (
              // Worksheet tab content
              <div className="h-full overflow-auto p-6">
                <ProposalWorksheetTab
                  formData={formData}
                  handleAddScopeOfWork={handleAddScopeOfWork}
                  handleAddCostCodes={handleAddCostCodes}
                  handleItemChange={handleItemChange}
                  handleDeleteItem={handleDeleteItem}
                  expandedItems={expandedItems}
                  toggleItemExpansion={toggleItemExpansion}
                  isReleased={isReleased}
                  proposalId={currentProposal?._id || editingProposal?._id}
                />
              </div>
            ) : (
              // Proposal preview tab content with split view
              <div className="h-full flex">
                {/* Left side - Controls */}
                <div className="w-64 border-r border-gray-200 p-4 overflow-y-auto">
                  <ProposalDisplaySettings
                    displaySettings={displaySettings}
                    handleDisplaySettingChange={handleDisplaySettingChange}
                  />
                </div>

                {/* Right side - Preview */}
                <div className="flex-1 overflow-auto">
                  <div className="max-w-4xl mx-auto overflow-auto p-6">
                    <div className="flex justify-end mb-4">
                      <button
                        onClick={handleDownloadProposal}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download PDF
                      </button>
                    </div>
                    {renderTemplate()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-white">
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Print
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                {editingProposal?.status ? (
                  <>
                    <button
                      onClick={handleSaveAsDraft}
                      disabled={isEditingDisabled(editingProposal.status)}
                      className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md 
                        ${isEditingDisabled(editingProposal.status) 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-gray-50'}`}
                    >
                      Save as Draft
                    </button>
                    
                    <button
                      onClick={handleSaveAndManualApprove}
                      disabled={isReleaseDisabled(editingProposal.status)}
                      className={`px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md 
                        ${isReleaseDisabled(editingProposal.status) 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-green-700'}`}
                    >
                      Manual Approve
                    </button>
                    
                    <button
                      onClick={handleSaveAndRelease}
                      disabled={isReleaseDisabled(editingProposal.status)}
                      className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md 
                        ${isReleaseDisabled(editingProposal.status) 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-blue-700'}`}
                    >
                      Save & Release
                    </button>

                    {editingProposal.status === 'released' && (
                      <button
                        onClick={handleUpdateReleasedProposal}
                        className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        Update Released Proposal
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleSaveAsDraft}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Save as Draft
                    </button>
                   
                    <button
                      onClick={handleSaveAndManualApprove}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                    >
                      Manual Approve
                    </button>
                    <button
                      onClick={handleSaveAndRelease}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                      Save & Release
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <AddScopeDialog
        isOpen={showAddScope}
        onClose={() => setShowAddScope(false)}
        onSave={handleSaveCostGroup}
        proposalId={currentProposal?._id || editingProposal?._id}
      />

      <AddCostCodeDialog
        isOpen={showAddCostCode}
        onClose={() => setShowAddCostCode(false)}
        onSave={handleSaveCostItem}
        proposalId={currentProposal?._id || editingProposal?._id}
      />
    </>
  );
};

export default CreateProposalDialog; 